import { AccountPhoneNumberStatusesEnum } from "dash-common";
import { Box, ModalContext, ViewerContext } from "dash-ui";
import { CONFIRM_SSO_NAME_MODAL } from "features/confirm-sso-name";
import Container from "flow-components/Container/Container";
import Footer from "flow-components/Footer/Footer";
import Header from "flow-components/Header/Header";
import { useContext, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { RoutesPath } from "../routes";

// What permissions the user needs to access these routes.
type RoutePermissions = {
  account: boolean;
  verifiedAccount: boolean;
  hasSubscription: boolean;
  ssoBlock?: boolean;
};

type IProtectedRouteProps = {
  children: JSX.Element;
  customPermissions?: RoutePermissions;
  redirectUrl?: string;
  showContent?: boolean;
  withContainer?: boolean;
  withProfileDropdown?: boolean;
  withBreadcrumbs?: boolean;
  withSidebar?: boolean;
};

function ProtectedRoute({
  children,
  customPermissions,
  showContent,
  withContainer = true,
  withProfileDropdown,
  withSidebar = true,
  withBreadcrumbs = true,
}: IProtectedRouteProps): JSX.Element | null {
  const { account, broker, profile } = useContext(ViewerContext);
  const { toggleModal, isOpenCurrent } = useContext(ModalContext);

  // Load permissions.
  // If no custom permissions were provided, enforce all protections.
  const permissions: RoutePermissions = customPermissions || {
    account: true,
    hasSubscription: true,
    verifiedAccount: true,
    ssoBlock: false,
  };

  // An SSO provider is free to hand us a first name and nothing else, which the
  // CRM used to paper over with a placeholder surname. Ask for the real one
  // instead, whichever provider signed the user in (DSH-939).
  const profileName = profile ?? account?.profile;
  const needsNameConfirmation =
    !!account?.ssoId && !!profileName && !profileName.lastName?.trim();

  // Decides whether the confirm type modal should be displayed - this is to
  // gather the user type information after an SSO account creation.
  // The name gate runs first, so the type answer syncs a contact that already
  // carries the confirmed name.
  useEffect(() => {
    if (!permissions.account || !account) return;

    if (needsNameConfirmation) {
      if (!isOpenCurrent[CONFIRM_SSO_NAME_MODAL]) {
        toggleModal(true, CONFIRM_SSO_NAME_MODAL);
      }
      return;
    }

    if (!broker && !isOpenCurrent["ConfirmUserTypeModal"]) {
      toggleModal(true, "ConfirmUserTypeModal");
    }
  });

  const isAccountVerified =
    account?.phone?.status === AccountPhoneNumberStatusesEnum.VERIFIED;

  if (permissions.account && !account) {
    // Avoids redirect loops by preserving an existing redirect param if present
    const params = new URLSearchParams(location.search);
    const redirectParam = params.get("redirect");

    const target = redirectParam
      ? redirectParam
      : location.pathname + location.search;

    const redirectUrl = `${RoutesPath.SignIn}?redirect=${encodeURIComponent(
      target
    )}`;

    return <Navigate to={redirectUrl} replace />;
  }

  if (permissions.verifiedAccount && !isAccountVerified) {
    if (
      permissions.ssoBlock &&
      account?.ssoId &&
      (!broker || (!account.isSeeker && !broker.license))
    ) {
      return <Navigate to={RoutesPath.CompleteSignUpSSO} />;
    }
    if (!account?.ssoId) {
      return <Navigate to={RoutesPath.CompleteSignUpCodeConfirmation} />;
    }
  }

  if (!withContainer) {
    return <>{children}</>;
  }

  // If the user has to confirm his type yet, we hide everything else, to prevent bugs
  if (
    isOpenCurrent["ConfirmUserTypeModal"] ||
    isOpenCurrent[CONFIRM_SSO_NAME_MODAL]
  )
    return (
      <>
        <Header withProfileDropdown={false} />
      </>
    );

  return (
    <Box h="100%" className="bg-slate-50">
      <Header withProfileDropdown={withProfileDropdown} withChecklist />
      <Container
        showContent={showContent}
        withBreadcrumbs={withBreadcrumbs}
        withSidebar={withSidebar}
      >
        {children}
      </Container>
      <Footer variation="light" />
    </Box>
  );
}

export default ProtectedRoute;
